import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isMobileBrowser } from '../../../base/environment/utils';
import { getLocalParticipant } from '../../../base/participants/functions';
import { isReactionsButtonEnabled, shouldDisplayReactionsButtons } from '../../../reactions/functions.web';
import { isCCTabEnabled } from '../../../subtitles/functions.any';
import { isTranscribing } from '../../../transcribing/functions';
import { setHangupMenuVisible, setOverflowMenuVisible, setToolbarHovered, setToolboxVisible } from '../../actions.web';
import { getJwtDisabledButtons, isToolboxVisible } from '../../functions.web';
import { useKeyboardShortcuts, useToolboxButtons } from '../../hooks.web';
import { groupBy } from "lodash";
import { BUTTONS_THRESHOLDS } from "../../../../11sight/features/toolbox/constants";
import { getAllToolboxButtons } from "../../functions.web";
import { isToolbarButtonEnabled } from "../../../base/config/functions.web";
import { useSelector } from "react-redux";
import AuditorNotesButton from "../../../../11sight/ui/components/toolbox/AuditorNotesButton";
import { makeStyles } from "tss-react/mui";
import { isLocalParticipantModerator } from "../../../base/participants/functions";
import HangupButton from "../../../../11sight/ui/components/toolbox/HangupButton";
import { EndConferenceButton } from "./EndConferenceButton";
import { LeaveConferenceButton } from "./LeaveConferenceButton";
import HangupMenuButton from "./HangupMenuButton";
import OverflowMenuButton from "./OverflowMenuButton";
import ContextMenu from "../../../base/ui/components/web/ContextMenu";
import Separator from "./Separator";
const NOT_APPLICABLE = "not-applicable";
const useStyles = makeStyles()((theme) => {
    const buttonGroupBaseStyles = {
        display: "flex",
        gap: theme.spacing(2)
    };
    return {
        toolboxButtonsWrapper: {
            display: "grid !important",
            gridTemplateColumns: "repeat(3, 1fr)",
            alignItems: "center",
            columnGap: theme.spacing(2),
            "&.only-center": {
                gridTemplateColumns: "initial"
            },
            "& > div": {
                whiteSpace: "nowrap"
            }
        },
        contextMenu: {
            position: "relative",
            right: "auto",
            margin: 0,
            marginBottom: "8px",
            maxHeight: "calc(100dvh - 100px)",
            minWidth: "240px"
        },
        hangupMenu: {
            position: "relative",
            right: "auto",
            display: "flex",
            flexDirection: "column",
            rowGap: "8px",
            margin: 0,
            padding: "16px",
            marginBottom: "4px"
        },
        buttonsGroupCenter: {
            ...buttonGroupBaseStyles,
            justifySelf: "center",
            gap: theme.spacing(3)
        },
        buttonsGroupStart: {
            ...buttonGroupBaseStyles,
            justifySelf: "start"
        },
        buttonsGroupEnd: {
            ...buttonGroupBaseStyles,
            justifySelf: "end"
        },
        buttonsGroupTopRight: {
            ...buttonGroupBaseStyles,
            position: "fixed",
            top: theme.spacing(2),
            right: theme.spacing(2)
        },
        buttonsGroupTopLeft: {
            ...buttonGroupBaseStyles,
            position: "fixed",
            top: theme.spacing(2),
            left: theme.spacing(2)
        },
        mobileContextWrapper: {
            background: isMobileBrowser() ? "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.64) 100%)!important" : "none"
        }
    };
});
/**
 * A component that renders the main toolbar.
 *
 * @param {IProps} props - The props of the component.
 * @returns {ReactElement}
 */
function Toolbox({ toolbarButtons, toolbarBackgroundColor: toolbarBackgroundColorProp }) {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const _toolboxRef = useRef(null);
    const conference = useSelector((state) => state['features/base/conference'].conference);
    const isNarrowLayout = useSelector((state) => state['features/base/responsive-ui'].isNarrowLayout);
    const videoSpaceWidth = useSelector((state) => state['features/base/responsive-ui'].videoSpaceWidth);
    const isModerator = useSelector(isLocalParticipantModerator);
    const customToolbarButtons = useSelector((state) => state['features/base/config'].customToolbarButtons);
    const iAmRecorder = useSelector((state) => state['features/base/config'].iAmRecorder);
    const iAmSipGateway = useSelector((state) => state['features/base/config'].iAmSipGateway);
    const overflowDrawer = useSelector((state) => state['features/toolbox'].overflowDrawer);
    const shiftUp = useSelector((state) => state['features/toolbox'].shiftUp);
    const overflowMenuVisible = useSelector((state) => state['features/toolbox'].overflowMenuVisible);
    const hangupMenuVisible = useSelector((state) => state['features/toolbox'].hangupMenuVisible);
    const buttonsWithNotifyClick = useSelector((state) => state['features/toolbox'].buttonsWithNotifyClick);
    const reduxToolbarButtons = useSelector((state) => state['features/toolbox'].toolbarButtons);
    const toolbarButtonsToUse = toolbarButtons || reduxToolbarButtons;
    const isDialogVisible = useSelector((state) => Boolean(state['features/base/dialog'].component));
    const localParticipant = useSelector(getLocalParticipant);
    const transcribing = useSelector(isTranscribing);
    const _isCCTabEnabled = useSelector(isCCTabEnabled);
    // Read toolbar background color from config (if provided) or from props.
    const toolbarBackgroundColorFromConfig = useSelector((state) => state['features/base/config'].toolbarConfig?.backgroundColor);
    const toolbarBackgroundColor = toolbarBackgroundColorProp || toolbarBackgroundColorFromConfig;
    // Do not convert to selector, it returns new array and will cause re-rendering of toolbox on every action.
    const jwtDisabledButtons = getJwtDisabledButtons(transcribing, _isCCTabEnabled, localParticipant?.features);
    const reactionsButtonEnabled = useSelector(isReactionsButtonEnabled);
    const _shouldDisplayReactionsButtons = useSelector(shouldDisplayReactionsButtons);
    const toolbarVisible = useSelector(isToolboxVisible);
    const mainToolbarButtonsThresholds = useSelector((state) => state['features/toolbox'].mainToolbarButtonsThresholds);
    const allButtons = useToolboxButtons(customToolbarButtons);
    const isMobile = isMobileBrowser();
    const endConferenceSupported = Boolean(conference?.isEndConferenceSupported() && isModerator);
    useKeyboardShortcuts(toolbarButtonsToUse);
    useEffect(() => {
        if (!toolbarVisible) {
            if (document.activeElement instanceof HTMLElement
                && _toolboxRef.current?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        }
    }, [toolbarVisible]);
    /**
     * Sets the visibility of the hangup menu.
     *
     * @param {boolean} visible - Whether or not the hangup menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetHangupVisible = useCallback((visible) => {
        dispatch(setHangupMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    /**
     * Sets the visibility of the overflow menu.
     *
     * @param {boolean} visible - Whether or not the overflow menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetOverflowVisible = useCallback((visible) => {
        dispatch(setOverflowMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    useEffect(() => {
        // On mobile web we want to keep both toolbox and hang up menu visible
        // because they depend on each other.
        if (endConferenceSupported && isMobile) {
            hangupMenuVisible && dispatch(setToolboxVisible(true));
        }
        else if (hangupMenuVisible && !toolbarVisible) {
            onSetHangupVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [dispatch, hangupMenuVisible, toolbarVisible, onSetHangupVisible]);
    useEffect(() => {
        if (overflowMenuVisible && isDialogVisible) {
            onSetOverflowVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [dispatch, overflowMenuVisible, isDialogVisible, onSetOverflowVisible]);
    /**
     * Key handler for overflow/hangup menus.
     *
     * @param {KeyboardEvent} e - Esc key click to close the popup.
     * @returns {void}
     */
    const onEscKey = useCallback((e) => {
        if (e?.key === 'Escape') {
            e?.stopPropagation();
            hangupMenuVisible && dispatch(setHangupMenuVisible(false));
            overflowMenuVisible && dispatch(setOverflowMenuVisible(false));
        }
    }, [dispatch, hangupMenuVisible, overflowMenuVisible]);
    /**
     * Dispatches an action signaling the toolbar is not being hovered.
     *
     * @private
     * @returns {void}
     */
    const onMouseOut = useCallback(() => {
        !overflowMenuVisible && dispatch(setToolbarHovered(false));
    }, [dispatch, overflowMenuVisible]);
    /**
     * Dispatches an action signaling the toolbar is being hovered.
     *
     * @private
     * @returns {void}
     */
    const onMouseOver = useCallback(() => {
        dispatch(setToolbarHovered(true));
    }, [dispatch]);
    /**
     * Handle focus on the toolbar.
     *
     * @returns {void}
     */
    const handleFocus = useCallback(() => {
        dispatch(setToolboxVisible(true));
    }, [dispatch]);
    /**
     * Handle blur the toolbar..
     *
     * @returns {void}
     */
    const handleBlur = useCallback(() => {
        dispatch(setToolboxVisible(false));
    }, [dispatch]);
    if (iAmRecorder || iAmSipGateway) {
        return null;
    }
    const toolbarAccLabel = 'toolbar.accessibilityLabel.moreActionsMenu';
    const containerClassName = `toolbox-content${isMobile || isNarrowLayout ? ' toolbox-content-mobile' : ''}`;
    const { mainMenuButtons, overflowMenuButtons } = getVisibleButtons({
        allButtons,
        buttonsWithNotifyClick,
        toolbarButtons: toolbarButtonsToUse,
        clientWidth: videoSpaceWidth,
        jwtDisabledButtons,
        mainToolbarButtonsThresholds
    });
    const raiseHandInOverflowMenu = overflowMenuButtons.some(({ key }) => key === 'raisehand');
    const showReactionsInOverflowMenu = _shouldDisplayReactionsButtons
        && ((!reactionsButtonEnabled && (raiseHandInOverflowMenu || isNarrowLayout || isMobile))
            || overflowMenuButtons.some(({ key }) => key === 'reactions'));
    const showRaiseHandInReactionsMenu = showReactionsInOverflowMenu && raiseHandInOverflowMenu;
    // rootClassNames will be defined by the extension
    const rootClassNames = `new-toolbox ${toolbarVisible ? 'visible' : ''} ${toolbarButtonsToUse.length ? '' : 'no-buttons'}`;
    function renderToolboxContent() {
        const toolbarAccLabel = "toolbar.accessibilityLabel.moreActionsMenu";
        const containerClassName = `toolbox-content${isMobile || isNarrowLayout ? " toolbox-content-mobile" : ""}`;
        const { mainMenuButtons, overflowMenuButtons } = getVisibleButtons();
        const raiseHandInOverflowMenu = overflowMenuButtons.some(({ key }) => key === "raisehand");
        const reactionsEnabledForRender = _shouldDisplayReactionsButtons;
        const showReactionsInOverflowMenu = (reactionsEnabledForRender && !reactionsButtonEnabled && (raiseHandInOverflowMenu || isNarrowLayout || isMobile)) || overflowMenuButtons.some(({ key }) => key === "reactions");
        const showRaiseHandInReactionsMenu = showReactionsInOverflowMenu && raiseHandInOverflowMenu;
        const overflowButton = !window.APP.isMeetingAuditor && Boolean(overflowMenuButtons.length) && (React.createElement(OverflowMenuButton, { ariaControls: "overflow-menu", buttons: overflowMenuButtons.reduce((acc, val) => {
                if (val.key === "reactions" && showReactionsInOverflowMenu) {
                    return acc;
                }
                if (val.key === "raisehand" && showRaiseHandInReactionsMenu) {
                    return acc;
                }
                if (acc.length) {
                    const prev = acc[acc.length - 1];
                    const group = prev[prev.length - 1].group;
                    if (group === val.group) {
                        prev.push(val);
                    }
                    else {
                        acc.push([val]);
                    }
                }
                else {
                    acc.push([val]);
                }
                return acc;
            }, []), isOpen: overflowMenuVisible, key: "overflow-menu", onToolboxEscKey: onEscKey, onVisibilityChange: onSetOverflowVisible, showRaiseHandInReactionsMenu: showRaiseHandInReactionsMenu, showReactionsMenu: showReactionsInOverflowMenu }));
        return ((React.createElement("div", { className: containerClassName },
            React.createElement("div", { className: "toolbox-content-wrapper", onFocus: handleFocus, ...(isMobile ? {} : {
                    onMouseOut,
                    onMouseOver
                }) },
                React.createElement("div", { className: cx(classes.toolboxButtonsWrapper, "toolbox-content-items", !mainMenuButtons.start && !mainMenuButtons.end && overflowMenuButtons.length === 0 && "only-center"), ref: _toolboxRef },
                    window.APP.isMeetingAuditor && React.createElement(AuditorNotesButton, null),
                    React.createElement("div", { className: cx(classes.buttonsGroupTopLeft) },
                        isMobile && overflowButton,
                        mainMenuButtons.topLeft?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key }))),
                    React.createElement("div", { className: cx(classes.buttonsGroupTopRight) }, mainMenuButtons.topRight?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key }))),
                    React.createElement("div", { className: cx(classes.buttonsGroupStart, "button-group-start") }, mainMenuButtons.start?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key }))),
                    React.createElement("div", { className: cx(classes.buttonsGroupCenter, "button-group-center painted-icons") },
                        mainMenuButtons.center?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key })),
                        isToolbarButtonEnabled("hangup", toolbarButtonsToUse) && (conference?.isEndConferenceSupported() && isModerator ? (React.createElement(HangupMenuButton, { ariaControls: "hangup-menu", isOpen: hangupMenuVisible, key: "hangup-menu", notifyMode: buttonsWithNotifyClick?.get("hangup-menu"), onVisibilityChange: onSetHangupVisible, colorInClass: true },
                            React.createElement(ContextMenu, { accessibilityLabel: toolbarAccLabel, className: classes.hangupMenu, hidden: false, inDrawer: overflowDrawer, onKeyDown: onEscKey },
                                React.createElement(EndConferenceButton, { buttonKey: "end-meeting", notifyMode: buttonsWithNotifyClick?.get("end-meeting") }),
                                React.createElement(LeaveConferenceButton, { buttonKey: "hangup", notifyMode: buttonsWithNotifyClick?.get("hangup") })))) : (React.createElement(HangupButton, { buttonKey: "hangup", customClass: "hangup-button", key: "hangup-button", notifyMode: buttonsWithNotifyClick?.get("hangup"), visible: isToolbarButtonEnabled("hangup", toolbarButtonsToUse) })))),
                    React.createElement("div", { className: cx(classes.buttonsGroupEnd, "button-group-end") },
                        mainMenuButtons.end?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key })),
                        !isMobile && overflowButton))))));
    }
    function getVisibleButtons() {
        if (window.APP.isMeetingAuditor)
            return {
                mainMenuButtons: {},
                overflowMenuButtons: []
            };
        const buttons = getAllToolboxButtons(customToolbarButtons);
        if (typeof window.APP !== "undefined" && buttonsWithNotifyClick?.size > 0) {
            Object.values(buttons).forEach((button) => {
                if (typeof button === "object") {
                    button.notifyMode = buttonsWithNotifyClick.get(button.key);
                }
            });
        }
        const isHangupVisible = isToolbarButtonEnabled("hangup", toolbarButtonsToUse);
        const { order } = BUTTONS_THRESHOLDS.find(({ width }) => videoSpaceWidth > width) || BUTTONS_THRESHOLDS[BUTTONS_THRESHOLDS.length - 1];
        const keys = Object.keys(buttons);
        const filtered = [
            ...order.map(key => buttons[key]).filter(button => button != null),
            ...Object.values(buttons).filter((button, index) => button != null && !order.includes(keys[index]))
        ].filter(({ key, alias = NOT_APPLICABLE, mobileOnly = false, desktopOnly = false, moderatorOnly = false }) => ((desktopOnly && !isMobile) || !desktopOnly) && ((mobileOnly && isMobile) || !mobileOnly) && ((moderatorOnly && isModerator) || !moderatorOnly) && !jwtDisabledButtons.includes(key) && (key === "noisesuppression" || key === "pip" || isToolbarButtonEnabled(key, toolbarButtonsToUse) || isToolbarButtonEnabled(alias, toolbarButtonsToUse)));
        const isVconnect = window.APP?.room?.vconnect || localStorage.getItem("isVconnect") === "true";
        const isVconnectCaller = window.APP?.room?.vconnectCaller || localStorage.getItem("isVconnectCaller") === "true";
        const hideForVconnectCaller = ["invite", "security", "recording", "livestreaming", "sharedvideo"];
        let customFiltered = filtered;
        if (isVconnect && isVconnectCaller && !isModerator) {
            customFiltered = filtered.filter(({ key }) => !hideForVconnectCaller.includes(key));
        }
        if (!isModerator) {
            const hideForAnonymous = ["videoquality", "sharedvideo"];
            customFiltered = customFiltered.filter(({ key }) => !hideForAnonymous.includes(key));
        }
        let sliceIndex = order.length + 2;
        if (isHangupVisible) {
            sliceIndex -= 1;
        }
        if (sliceIndex < customFiltered.length) {
            sliceIndex -= 1;
        }
        return {
            mainMenuButtons: groupBy(customFiltered.slice(0, sliceIndex), "alignment"),
            overflowMenuButtons: customFiltered.slice(sliceIndex)
        };
    }
    const _addBackground = arguments[0]?.addBackground !== undefined ? arguments[0].addBackground : true;
    const _chatOpen = useSelector((state) => state["features/chat"].isOpen);
    return ((React.createElement("div", { className: cx(`new-toolbox ${toolbarVisible ? "visible" : ""} ${toolbarButtonsToUse.length ? "" : "no-buttons"} ${_chatOpen ? "shift-right" : ""} ${_addBackground !== false ? classes.mobileContextWrapper : ""}`, shiftUp && "shift-up"), id: "new-toolbox" }, renderToolboxContent())));
}
export default Toolbox;
export { Toolbox };
